import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw, Sparkles } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useToast } from '@/components/ui/use-toast';

interface BatchJob {
  id: string;
  status: string;
  total_products: number;
  processed_products: number;
  succeeded_products: number;
  failed_products: number;
  category_path?: string;
  estimated_cost?: number;
  actual_cost?: number;
  created_at: string;
  submitted_at?: string;
  completed_at?: string;
  error_message?: string;
}

interface EnrichmentBatchProgressProps {
  siteId: string;
  onJobComplete?: () => void;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  preparing: { color: 'bg-yellow-500', icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Preparing' },
  uploading: { color: 'bg-blue-500', icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Uploading' },
  queued: { color: 'bg-blue-500', icon: <Clock className="h-3 w-3" />, label: 'Queued' },
  validating: { color: 'bg-blue-500', icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Validating' },
  in_progress: { color: 'bg-blue-500', icon: <Sparkles className="h-3 w-3" />, label: 'Processing' },
  processing_results: { color: 'bg-purple-500', icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Applying Results' },
  completed: { color: 'bg-green-500', icon: <CheckCircle className="h-3 w-3" />, label: 'Completed' },
  failed: { color: 'bg-red-500', icon: <XCircle className="h-3 w-3" />, label: 'Failed' },
  cancelled: { color: 'bg-gray-500', icon: <XCircle className="h-3 w-3" />, label: 'Cancelled' },
};

export function EnrichmentBatchProgress({ siteId, onJobComplete }: EnrichmentBatchProgressProps) {
  const [jobs, setJobs] = useState<BatchJob[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchJobs = async () => {
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/batch-jobs?siteId=${siteId}`);
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs || []);

        // Check if any job just completed
        const activeStatuses = ['preparing', 'uploading', 'queued', 'validating', 'in_progress', 'processing_results'];
        const hasActiveJobs = data.jobs?.some((j: BatchJob) => activeStatuses.includes(j.status));
        if (!hasActiveJobs && onJobComplete) {
          onJobComplete();
        }
      }
    } catch (error) {
      console.error('Error fetching batch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerCheck = async (jobId: string) => {
    try {
      await authFetch(`${API_URL}/product-enrichment/batch-jobs/${jobId}/check`, {
        method: 'POST',
      });
      fetchJobs();
      toast({
        title: 'Status Check Triggered',
        description: 'Checking batch status with OpenAI...',
      });
    } catch (error) {
      console.error('Error triggering check:', error);
    }
  };

  useEffect(() => {
    fetchJobs();

    // Poll for updates every 30 seconds while there are active jobs
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [siteId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading batch jobs...
      </div>
    );
  }

  const activeJobs = jobs.filter(j =>
    ['preparing', 'uploading', 'queued', 'validating', 'in_progress', 'processing_results'].includes(j.status)
  );

  if (activeJobs.length === 0 && jobs.length === 0) {
    return null; // Don't show anything if no jobs
  }

  // Show active jobs first, then recent completed ones
  const displayJobs = [
    ...activeJobs,
    ...jobs.filter(j => j.status === 'completed' || j.status === 'failed').slice(0, 3)
  ];

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          Batch Enrichment Jobs
        </CardTitle>
        <CardDescription className="text-xs">
          Using OpenAI Batch API for 50% cost savings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayJobs.map((job) => {
          const config = STATUS_CONFIG[job.status] || STATUS_CONFIG.preparing;
          const progress = job.total_products > 0
            ? Math.round((job.processed_products / job.total_products) * 100)
            : 0;
          const isActive = ['preparing', 'uploading', 'queued', 'validating', 'in_progress', 'processing_results'].includes(job.status);

          return (
            <div key={job.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`${config.color} text-white`}>
                    {config.icon}
                    <span className="ml-1">{config.label}</span>
                  </Badge>
                  {job.category_path && (
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {job.category_path.split('>').pop()?.trim()}
                    </span>
                  )}
                </div>
                {isActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => triggerCheck(job.id)}
                    className="h-6 px-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {job.processed_products} / {job.total_products} products
                    {job.failed_products > 0 && ` (${job.failed_products} failed)`}
                  </span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-1.5" />
              </div>

              {job.error_message && (
                <p className="text-xs text-red-500 mt-1">
                  {job.error_message}
                </p>
              )}

              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  Started: {new Date(job.created_at).toLocaleString()}
                </span>
                {job.completed_at && (
                  <span>
                    Completed: {new Date(job.completed_at).toLocaleString()}
                  </span>
                )}
                {job.estimated_cost && !job.actual_cost && (
                  <span>Est. cost (extraction only): ${job.estimated_cost.toFixed(4)}</span>
                )}
                {job.actual_cost && (
                  <span>Cost: ${job.actual_cost.toFixed(4)}</span>
                )}
              </div>
            </div>
          );
        })}

        {activeJobs.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            OpenAI Batch API processes within 24 hours. Progress updates every 30 seconds.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default EnrichmentBatchProgress;
